import React, { useCallback, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { useLocation, useNavigate } from 'react-router-dom';
import { PayloadAction } from '@reduxjs/toolkit';
import { Button, Col, Row, Select, Spin } from 'antd';
import pluralize from 'pluralize';
import queryString from 'query-string';
import { useDidUpdate } from 'rooks';
import cx from 'classnames';

import {
  BOAccountViewModel,
  BOUserListResponseModel,
  BOUserRequestStatusEnum,
  BOUserTypeFilterEnum,
} from 'api/adminApiClient';
import {
  MemberFilterTypes,
  MembersPageType,
} from 'features/admin/member/enums';
import {
  getMemberTypeOptions,
  getRecruiterEntityName,
} from 'utils/recruiterUtils';
import BaseSearch from 'components/wrappers/BaseSearch';
import RecruiterCard from 'features/admin/member/RecruiterCard';
import {
  getAllRecruiters,
  getMemberListExcel,
} from 'redux/reducers/recruiterSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { typeConversionToNumberArray } from 'utils/convertUtils';

import styles from './AdminMembersSection.module.less';

interface EmptyMembersMessages {
  noCompanyMembers: string;
  noSelfEmployedMembers: string;
  noInternationMembers: string;
  noAllMembers: string;
}

type MemberMessagesType = MembersPageType.Details | MembersPageType.Requests;

const EMPTY_MEMBERS_LISTS_MESSAGES: Record<
  MemberMessagesType,
  EmptyMembersMessages
> = {
  [MembersPageType.Requests]: {
    noCompanyMembers: 'There are no company member requests in the system.',
    noSelfEmployedMembers:
      'There are no self-employed member requests in the system.',
    noInternationMembers:
      'There are no international member requests in the system.',
    noAllMembers: 'There are no member requests in the system.',
  },
  [MembersPageType.Details]: {
    noCompanyMembers: 'There are no company members registered in the system.',
    noSelfEmployedMembers:
      'There are no self-employed members registered in the system.',
    noInternationMembers:
      'There are no international members registered in the system.',
    noAllMembers: 'There are no members registered in the system.',
  },
};

const entities = [
  {
    label: getRecruiterEntityName(BOUserTypeFilterEnum.All),
    value: BOUserTypeFilterEnum.All,
  },
  {
    label: getRecruiterEntityName(BOUserTypeFilterEnum.Company),
    value: BOUserTypeFilterEnum.Company,
  },
  {
    label: getRecruiterEntityName(BOUserTypeFilterEnum.SelfEmployed),
    value: BOUserTypeFilterEnum.SelfEmployed,
  },
  {
    label: getRecruiterEntityName(BOUserTypeFilterEnum.International),
    value: BOUserTypeFilterEnum.International,
  },
];

type Props = {
  pageType: MembersPageType;
};

interface MemberFilters {
  [MemberFilterTypes.Query]: string;
  [MemberFilterTypes.Section]?: string;
  [MemberFilterTypes.Entity]: BOUserTypeFilterEnum;
  [MemberFilterTypes.ProfileTypes]: number[];
}

export default function AdminMembersSection(props: Props) {
  const { pageType } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { query, entity, section, profileTypes } = queryString.parse(
    location.search,
  );
  const initialFilters: MemberFilters = {
    [MemberFilterTypes.Query]: query,
    [MemberFilterTypes.Entity]: +entity || BOUserTypeFilterEnum.All,
    [MemberFilterTypes.ProfileTypes]: typeConversionToNumberArray(profileTypes),
  };
  const latestRequestId = useRef(0);
  const infiniteScrollParentRef = useRef(null);
  const dispatch = useAppDispatch();
  const [members, setMembers] = useState<BOAccountViewModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [filters, setFilters] = useState<MemberFilters>(initialFilters);
  const requestType =
    pageType === MembersPageType.Requests
      ? BOUserRequestStatusEnum.MembersRequests
      : BOUserRequestStatusEnum.Members;
  const isVisibleImportMemberListBtn = location.pathname === '/members';

  useDidUpdate(() => {
    const queryData: MemberFilters = {
      [MemberFilterTypes.Query]: query,
      [MemberFilterTypes.Section]: section,
      [MemberFilterTypes.Entity]: filters.entity,
      [MemberFilterTypes.ProfileTypes]: filters.profileTypes,
    };
    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
  }, [filters, query]);
  useEffect(() => {
    initInfinityScroll();
  }, [filters, query]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setMembers([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const onSetMembers = useCallback(
    (result: PayloadAction<BOUserListResponseModel>) => {
      const loadedMembers = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const newMembers = [...members, ...loadedMembers];
      const hasMoreRows = newMembers.length < totalCount;

      setHasMore(hasMoreRows);
      setMembers(newMembers);
      setTotalCount(totalCount);
    },
    [members],
  );

  const onLoadMore = useCallback(
    async (pageNumber: number) => {
      const requestId = Date.now();
      latestRequestId.current = requestId;

      const result = await dispatch(
        getAllRecruiters({
          requestId,
          pageNumber,
          searchQuery: query ? query.trim() : '',
          requestType,
          entityFilter: filters[MemberFilterTypes.Entity],
          profileTypes: filters[MemberFilterTypes.ProfileTypes],
        }),
      );

      if (result.meta.arg.requestId === latestRequestId.current) {
        if (getAllRecruiters.fulfilled.match(result)) {
          onSetMembers(result);
        } else {
          setHasMore(false);
        }

        setIsInitialDataLoaded(true);
      }
    },
    [dispatch, onSetMembers, filters, requestType, query],
  );

  const getNoMembersMessage = () => {
    const membersMessages =
      EMPTY_MEMBERS_LISTS_MESSAGES[pageType as MemberMessagesType];

    switch (filters.entity) {
      case BOUserTypeFilterEnum.Company:
        return membersMessages.noCompanyMembers;

      case BOUserTypeFilterEnum.SelfEmployed:
        return membersMessages.noSelfEmployedMembers;

      case BOUserTypeFilterEnum.International:
        return membersMessages.noInternationMembers;

      case BOUserTypeFilterEnum.All:
      default:
        return membersMessages.noAllMembers;
    }
  };

  const handleFilterChange =
    (type: MemberFilterTypes) => (value: BOUserTypeFilterEnum | number[]) => {
      setFilters({
        ...filters,
        [type]: value,
      });
    };

  const handleDownloadMemberList = async () => {
    const result = await dispatch(
      getMemberListExcel({
        memberName: filters[MemberFilterTypes.Query]
          ? filters[MemberFilterTypes.Query].trim()
          : '',
        requestType,
        entityFilter: filters[MemberFilterTypes.Entity],
        profileTypes: filters[MemberFilterTypes.ProfileTypes],
      }),
    );

    if (getMemberListExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const pluralizedMembers = pluralize('member', totalCount, true);

  return (
    <div ref={infiniteScrollParentRef} className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          <Row gutter={[20, 10]}>
            <Col xs={24} md={6} data-id="member-search">
              <BaseSearch
                searchQuery={query}
                placeholder="Search by member name, ID"
              />
            </Col>

            <Col xs={24} md={6} data-id="member-type-dropdown">
              <Select
                className="full-width"
                value={filters[MemberFilterTypes.ProfileTypes]}
                mode="multiple"
                optionFilterProp="children"
                placeholder="Select member type"
                onChange={handleFilterChange(MemberFilterTypes.ProfileTypes)}
                filterOption
              >
                {getMemberTypeOptions().map((memberType) => (
                  <Select.Option
                    key={`select-member-type-${memberType.value}`}
                    value={memberType.value}
                  >
                    {memberType.label}
                  </Select.Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} md={6} data-id="entity-type-dropdown">
              <Select
                value={filters[MemberFilterTypes.Entity]}
                className="full-width"
                placeholder="Select entity"
                onChange={handleFilterChange(MemberFilterTypes.Entity)}
              >
                {entities.map((entity) => (
                  <Select.Option
                    key={`select-entity-${entity.value}`}
                    value={entity.value}
                  >
                    {entity.label}
                  </Select.Option>
                ))}
              </Select>
            </Col>

            {isVisibleImportMemberListBtn && (
              <Col xs={24} md={6}>
                <Button
                  className={cx('with-icon', styles.btn)}
                  onClick={handleDownloadMemberList}
                  icon={<span className="icon icon-xls-file" />}
                >
                  Export Member List
                </Button>
              </Col>
            )}

            <Col xs={24}>
              {isInitialDataLoaded && (
                <div className="page-inner-container">
                  {!!totalCount && (
                    <p className="page-total-count">{pluralizedMembers}</p>
                  )}

                  {!totalCount && (
                    <span className="no-data-msg">{getNoMembersMessage()}</span>
                  )}
                </div>
              )}
            </Col>
          </Row>
          {isVisible && (
            <InfiniteScroll
              pageStart={-1}
              loadMore={onLoadMore}
              hasMore={hasMore}
              loader={<Spin key={0} />}
              useWindow={false}
              getScrollParent={() => infiniteScrollParentRef.current}
            >
              <Row gutter={[20, 20]}>
                {members.map((member, index) => (
                  <Col
                    xs={24}
                    md={12}
                    className="d-flex"
                    key={`${member.userId}-${index}`}
                  >
                    <RecruiterCard recruiter={member} />
                  </Col>
                ))}
              </Row>
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
}
